import { Device } from "@capacitor/device";
import type {
  DeviceId,
  DeviceInfo as CapacitorDeviceInfo,
  GetLanguageCodeResult
} from "@capacitor/device";
import { Network } from "@capacitor/network";
import type { ConnectionStatus } from "@capacitor/network";
import { PLATFORM } from "src/hybrid/components/Platform";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { appVersionState } from "src/state/state";
import type { DeviceInfo } from "../types";
import { collectPermissions } from "./collectPermissions";

/**
 * Safe wrapper for async plugin calls
 * Returns null on error instead of throwing
 */
async function safeCall<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (error) {
    reportErrorSentry(error);
    return null;
  }
}

/**
 * Collects comprehensive device information from Capacitor plugins
 * All API calls run in parallel for performance
 */
export async function collectDeviceInfo(): Promise<DeviceInfo> {
  // Collect all device information in parallel
  const results = await Promise.all([
    safeCall<CapacitorDeviceInfo>(() => Device.getInfo()),
    safeCall<ConnectionStatus>(() => Network.getStatus()),
    safeCall<DeviceId>(() => Device.getId()),
    safeCall<GetLanguageCodeResult>(() => Device.getLanguageCode()),
    collectPermissions()
  ]);

  const [deviceInfo, networkStatus, deviceId, languageCode, permissions] =
    results;

  // Get timezone from Intl API
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    // System Information
    platform: PLATFORM,
    operatingSystem: deviceInfo?.operatingSystem ?? "unknown",
    osVersion: deviceInfo?.osVersion ?? "unknown",
    appVersion: appVersionState.currentVersionString,
    appBuild: appVersionState.state.native,
    webViewVersion: deviceInfo?.webViewVersion ?? "unavailable",
    locale: languageCode?.value ?? navigator.language,
    timezone,

    // Hardware
    manufacturer: deviceInfo?.manufacturer ?? "unavailable",
    model: deviceInfo?.model ?? "unavailable",
    deviceId: deviceId?.identifier ?? "unavailable",
    isVirtual: deviceInfo?.isVirtual ?? false,

    // Network
    networkConnected: networkStatus?.connected ?? false,
    networkType: networkStatus?.connectionType ?? "unknown",

    // Permissions
    pushNotificationStatus: permissions.pushNotificationStatus,
    biometricAvailable: permissions.biometricAvailable
  };
}
